import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Slider } from "../components/ui/slider";
import { Compass, ChevronRight, ChevronLeft, Save, CheckCircle } from "lucide-react";
import ExerciseIntro from "../components/ExerciseIntro";
import { getExerciseInfo } from "../lib/exerciseData";
import { API } from "../App";
import { useI18n } from "../lib/i18n";
import ShareExerciseDialog from "../components/ShareExerciseDialog";
import { useSharePrompt } from "../hooks/useSharePrompt";

const ValuesExplorationPage = ({ user, logout }) => {
  const { t } = useI18n();
  const { shareData, promptShare, closeSharePrompt } = useSharePrompt();
  const EXERCISE_INFO = getExerciseInfo(t);
  const [currentDomain, setCurrentDomain] = useState(0);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const VALUE_DOMAINS = [
    { id: "family", name: t("values.domain.family"), description: t("values.domain.family.desc") },
    { id: "relationships", name: t("values.domain.relationships"), description: t("values.domain.relationships.desc") },
    { id: "friends", name: t("values.domain.friends"), description: t("values.domain.friends.desc") },
    { id: "work", name: t("values.domain.work"), description: t("values.domain.work.desc") },
    { id: "education", name: t("values.domain.education"), description: t("values.domain.education.desc") },
    { id: "leisure", name: t("values.domain.leisure"), description: t("values.domain.leisure.desc") },
    { id: "spirituality", name: t("values.domain.spirituality"), description: t("values.domain.spirituality.desc") },
    { id: "community", name: t("values.domain.community"), description: t("values.domain.community.desc") },
    { id: "health", name: t("values.domain.health"), description: t("values.domain.health.desc") },
    { id: "environment", name: t("values.domain.environment"), description: t("values.domain.environment.desc") },
  ];

  const domain = VALUE_DOMAINS[currentDomain];

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/values-exploration`, { responses }, { withCredentials: true });
      toast.success(t("values.saved"));
      if (user?.role === "patient") promptShare(res.data, "values_exploration");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSubmitting(false); }
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-8" data-testid="values-exploration-page">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("values.title")}</h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("values.pageSubtitle")}</p>
        </div>

        <ExerciseIntro exerciseInfo={EXERCISE_INFO.values_exploration} />

        {/* Domain selector */}
        <div className="flex flex-wrap gap-2 justify-center">
          {VALUE_DOMAINS.map((d, idx) => (
            <button
              key={d.id}
              onClick={() => setCurrentDomain(idx)}
              className={`px-3 py-1.5 rounded-full text-sm transition-all border ${
                currentDomain === idx ? "bg-primary text-white border-primary" : "bg-background border-border hover:bg-muted"
              }`}
              data-testid={`domain-${d.id}`}
            >
              {d.name}
            </button>
          ))}
        </div>

        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Compass className="w-5 h-5" />
              {t("values.domainOf", { current: currentDomain + 1, total: VALUE_DOMAINS.length })} — {domain.name}
            </CardTitle>
            <p className="text-sm text-muted-foreground text-sm sm:text-base mt-1">{domain.description}</p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("values.whatImportant")}</label>
              <Textarea
                placeholder={t("values.importancePlaceholder")}
                value={responses[domain.id]?.importance || ""}
                onChange={(e) => setResponses(prev => ({
                  ...prev,
                  [domain.id]: { ...prev[domain.id], importance: e.target.value },
                }))}
                rows={3}
                data-testid={`importance-${domain.id}`}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">{t("values.howToAct")}</label>
              <Textarea
                placeholder={t("values.actionsPlaceholder")}
                value={responses[domain.id]?.actions || ""}
                onChange={(e) => setResponses(prev => ({
                  ...prev,
                  [domain.id]: { ...prev[domain.id], actions: e.target.value },
                }))}
                rows={3}
                data-testid={`actions-${domain.id}`}
              />
            </div>

            <div className="space-y-3">
              <label className="text-sm font-medium">{t("values.ratingQuestion")}</label>
              <Slider
                value={[responses[domain.id]?.rating ?? 5]}
                onValueChange={(value) => setResponses(prev => ({
                  ...prev,
                  [domain.id]: { ...prev[domain.id], rating: value[0] },
                }))}
                min={1}
                max={10}
                step={1}
                data-testid={`rating-${domain.id}`}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>1</span>
                <span>{responses[domain.id]?.rating ?? 5}/10</span>
                <span>10</span>
              </div>
            </div>

            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" disabled={currentDomain === 0} onClick={() => setCurrentDomain(currentDomain - 1)}>
                <ChevronLeft className="w-4 h-4 mr-1" />
              </Button>
              {currentDomain < VALUE_DOMAINS.length - 1 ? (
                <Button onClick={() => setCurrentDomain(currentDomain + 1)}>
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={submitting} className="bg-primary hover:bg-primary/90" data-testid="save-values">
                  <Save className="w-4 h-4 mr-1" /> {submitting ? t("exercise.saving") : t("common.save")}
                </Button>
              )}
            </div>

            <p className="text-xs text-center text-muted-foreground">
              {t("values.domainsExplored")}: {Object.keys(responses).length}/{VALUE_DOMAINS.length}
            </p>
          </CardContent>
        </Card>
      </div>
      <ShareExerciseDialog open={!!shareData} entryId={shareData?.entryId} exerciseType={shareData?.exerciseType} onClose={closeSharePrompt} />
    </DashboardLayout>
  );
};

export default ValuesExplorationPage;
